import React from 'react';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { showErrorNotification, showSuccessNotification } from 'src/components/widgets/notificationToaster';
import { dataURLtoFile, T, uploadComposerPreviewImage } from 'src/shared/helpers';
import MaterialIcon from 'src/shared/material_icons/image/palette.svg';
import ScreenshotIcon from 'src/shared/material_icons/image/photo_camera.svg';
import styled from 'styled-components';
import { BasicButton } from '../../../../../../widgets/buttons';

interface Composer3DPreviewCaptureProps {
    materials: BABYLON.Material[];
    onImagePick: (imageUrl: string, tempImageFileKey?: string) => void | Promise<void | string>;
    successMessage: string;
    helperText?: string;
    disabled?: boolean;
}

const CapturePanel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
    padding: 10px;
    border: 1px solid var(--composer-line, #dce3eb);
    border-radius: 8px;
    background: var(--composer-sunken, #f4f7fa);
`;

const CaptureDescription = styled.p`
    margin: 0;
    color: var(--composer-faint, #677078);
    font-size: 11px;
    line-height: 16px;
`;

const CaptureRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;

    > select {
        min-width: 0;
        flex: 1;
        height: 30px;
        padding: 0 8px;
        border: 1px solid var(--composer-line, #dce3eb);
        border-radius: 7px;
        background: var(--composer-panel, #fff);
        color: var(--composer-fg, #1e2a38);
        font-size: 11.5px;
    }
`;

const CaptureStatus = styled.span`
    color: var(--composer-muted, #5c6b7a);
    font-size: 11px;
`;

const Composer3DPreviewCapture: React.FC<Composer3DPreviewCaptureProps> = ({ materials, onImagePick, successMessage, helperText, disabled = false }) => {
    const [sceneEditor] = usePreview3DStore((store) => [store.sceneEditor]);
    const [selectedMaterialID, setSelectedMaterialID] = React.useState('');
    const [capturing, setCapturing] = React.useState<'object' | 'material' | null>(null);

    React.useEffect(() => {
        if (selectedMaterialID && materials.some((material) => material.id === selectedMaterialID)) return;
        setSelectedMaterialID(materials[0]?.id ?? '');
    }, [materials, selectedMaterialID]);

    const capture = async (type: 'object' | 'material') => {
        if (!sceneEditor || capturing) return;
        if (type === 'material' && !selectedMaterialID) {
            showErrorNotification({ message: T._('Select a material before taking its screenshot.', 'Admin') });
            return;
        }

        setCapturing(type);
        try {
            const imageData =
                type === 'material'
                    ? await sceneEditor.getMaterialScreenshotAsync(selectedMaterialID, 512, 512)
                    : await sceneEditor.getSceneContentScreenshotAsync(512, 512);
            if (!imageData) throw new Error('The 3D editor did not return an image.');

            const fileName = type === 'material' ? `${selectedMaterialID}-preview.png` : 'object-preview.png';
            const image = await uploadComposerPreviewImage(dataURLtoFile(imageData, fileName));

            await onImagePick(image.url, image.key);
            showSuccessNotification({ message: successMessage });
        } catch (error) {
            console.error('Composer 3D preview capture failed:', error);
            showErrorNotification({ message: T._('The 3D preview image could not be generated. Try again.', 'Admin') });
        } finally {
            setCapturing(null);
        }
    };

    const controlsDisabled = disabled || !sceneEditor || capturing !== null;

    return (
        <CapturePanel>
            <CaptureDescription>
                {helperText ?? T._('Use the current 3D view or a material preview instead of uploading an image.', 'Admin')}
            </CaptureDescription>
            <CaptureRow>
                <BasicButton type='button' disabled={controlsDisabled} onClick={() => void capture('object')}>
                    <ScreenshotIcon />
                    {T._('Use object', 'Admin')}
                </BasicButton>
            </CaptureRow>
            <CaptureRow>
                <select
                    aria-label={T._('Material for preview image', 'Admin')}
                    value={selectedMaterialID}
                    disabled={controlsDisabled || materials.length === 0}
                    onChange={(event) => setSelectedMaterialID(event.target.value)}
                >
                    {materials.length === 0 ? (
                        <option value=''>{T._('No materials available', 'Admin')}</option>
                    ) : (
                        materials.map((material) => (
                            <option key={material.id} value={material.id}>
                                {material.name || material.id}
                            </option>
                        ))
                    )}
                </select>
                <BasicButton type='button' disabled={controlsDisabled || !selectedMaterialID} onClick={() => void capture('material')}>
                    <MaterialIcon />
                    {T._('Use material', 'Admin')}
                </BasicButton>
            </CaptureRow>
            {capturing && <CaptureStatus>{T._('Generating and uploading the preview…', 'Admin')}</CaptureStatus>}
            {!sceneEditor && <CaptureStatus>{T._('The 3D editor must be loaded before taking a screenshot.', 'Admin')}</CaptureStatus>}
        </CapturePanel>
    );
};

export default Composer3DPreviewCapture;
